import React, {useEffect, useRef, useState} from "react";
import {Button, message, Spin, DatePicker, Alert, Space, ConfigProvider} from "antd";
import {useStore} from "@vigo/hooks";
import moment from "moment-timezone";
import trTR from "antd/es/locale/tr_TR";

export const WalletActivationModal = ({visible, onCancel, onConfirm, courierId}) => {
    const stores = useStore();
    const [loading, setLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [processCreditLogs, setProcessCreditLogs] = useState(true);
    const [creditLogStartDate, setCreditLogStartDate] = useState(null);
    const [dateError, setDateError] = useState("");
    const [creditLogSummary, setCreditLogSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (visible && courierId) {
            loadActivationPreview();
            setCreditLogStartDate(null);
            setDateError("");
        }
    }, [visible]);

    const loadActivationPreview = async () => {
        if (!isMountedRef.current) return;

        setLoading(true);
        try {
            const result = await stores.walletStore.getWalletActivationPreview(courierId);
            if (!isMountedRef.current) return;

            if (result && result.success) {
                setPreviewData(result.data);
                setProcessCreditLogs(true);
                // Son deaktivasyon tarihini varsayılan olarak ayarla ve otomatik credit log özetini yükle
                if (result.data?.lastDeactivateDate) {
                    const lastDeactivateDate = moment(result.data.lastDeactivateDate);
                    setCreditLogStartDate(lastDeactivateDate);
                    // Modal açıldığında otomatik olarak credit log özetini yükle
                    await loadCreditLogSummary(lastDeactivateDate);
                }
            } else {
                message.error("Aktivasyon önizlemesi alınamadı");
            }
        } catch (error) {
            if (!isMountedRef.current) return;
            message.error("Aktivasyon önizlemesi alınırken hata oluştu");
            console.error("Error loading activation preview:", error);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    const handleDateChange = async (date) => {
        setCreditLogStartDate(date);
        setDateError("");
        setCreditLogSummary(null);
        
        // Sadece credit log işlenecekse tarih validasyonu yap
        if (processCreditLogs && date && previewData?.lastDeactivateDate) {
            const selectedDate = moment(date);
            const lastDeactivateDate = moment(previewData.lastDeactivateDate);
            
            if (!selectedDate.isSameOrAfter(lastDeactivateDate, 'minute')) {
                setDateError("Credit log başlangıç tarihi, cüzdanın son kapanma tarihinden eski olamaz.");
                return;
            }
        }

        // Tarih seçildiğinde credit log özetini al (sadece credit log işlenecekse)
        if (processCreditLogs && date) {
            await loadCreditLogSummary(date);
        }
    };

    const loadCreditLogSummary = async (date) => {
        if (!isMountedRef.current) return;

        setSummaryLoading(true);
        try {
            const customStartDate = moment(date).format("YYYY-MM-DD");
            const customStartTime = moment(date).format("HH:mm");
            
            const result = await stores.walletStore.getWalletActivationPreview(courierId, {
                customStartDate,
                customStartTime
            });
            
            if (!isMountedRef.current) return;
            
            if (result && result.success) {
                setCreditLogSummary(result.data);
            } else {
                setCreditLogSummary(null);
            }
        } catch (error) {
            if (!isMountedRef.current) return;
            console.error("Error loading credit log summary:", error);
            setCreditLogSummary(null);
        } finally {
            if (isMountedRef.current) {
                setSummaryLoading(false);
            }
        }
    };

    const handleConfirm = () => {
        if (processCreditLogs && !creditLogStartDate) {
            message.error("Credit log'ları işlemek için başlangıç tarihi seçmelisiniz.");
            return;
        }
        
        if (dateError) {
            message.error(dateError);
            return;
        }
        
        // Eğer credit log yoksa da tarih seçilmişse, backend'e gönder
        onConfirm(processCreditLogs, creditLogStartDate);
    };

    const formatCurrency = amount => {
        return new Intl.NumberFormat("tr-TR", {
            style: "currency",
            currency: "TRY"
        }).format(amount);
    };

    if (!visible) {
        return null;
    }

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}
        >
            <div
                style={{
                    backgroundColor: "white",
                    padding: "24px",
                    borderRadius: "12px",
                    width: processCreditLogs ? "800px" : "500px",
                    minHeight: "400px",
                    maxHeight: "90vh",
                    overflow: "auto",
                    position: "relative",
                    zIndex: 1001,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    transition: "width 0.3s ease"
                }}
            >
                <div style={{marginBottom: "24px"}}>
                    <h2 style={{margin: 0, fontSize: "20px", fontWeight: "600", color: "#262626"}}>
                        Cüzdan Aktivasyonu
                    </h2>
                </div>
            {loading ? (
                <div style={{textAlign: "center", padding: "40px"}}>
                    <Spin size="large" />
                    <div style={{marginTop: "16px"}}>Aktivasyon bilgileri yükleniyor...</div>
                </div>
            ) : previewData ? (
                <Space direction="vertical" size="large" style={{width: "100%"}}>
                    <div 
                        style={{
                            padding: "16px",
                            backgroundColor: "#fafafa",
                            borderRadius: "8px",
                            border: "1px solid #e8e8e8"
                        }}
                    >
                        <div style={{fontSize: "14px", color: "#666", marginBottom: "4px"}}>
                            Son Kapanma Tarihi
                        </div>
                        <div style={{fontSize: "16px", fontWeight: "500", color: "#262626"}}>
                            {previewData.lastDeactivateDate
                                ? moment(previewData.lastDeactivateDate).format("DD.MM.YYYY HH:mm")
                                : "Bilinmiyor"}
                        </div>
                    </div>


                    <div 
                        style={{
                            padding: "16px",
                            backgroundColor: "#f8f9fa",
                            borderRadius: "8px",
                            border: "1px solid #e9ecef"
                        }}
                    >
                        <label style={{display: "flex", alignItems: "center", cursor: "pointer"}}>
                            <input
                                type="checkbox"
                                checked={processCreditLogs}
                                onChange={async (e) => {
                                    const isChecked = e.target.checked;
                                    setProcessCreditLogs(isChecked);
                                    
                                    // Checkbox işaretlendiğinde otomatik credit log yükle
                                    if (isChecked && creditLogStartDate) {
                                        await loadCreditLogSummary(creditLogStartDate);
                                    }
                                }}
                                style={{
                                    marginRight: "12px",
                                    transform: "scale(1.2)"
                                }}
                            />
                            <span style={{fontSize: "16px", fontWeight: "500", color: "#262626"}}>
                                İşlenmemiş credit log'ları cüzdana yansıt
                            </span>
                        </label>
                    </div>
                    
                    {processCreditLogs && (
                        <div 
                            style={{
                                padding: "20px",
                                backgroundColor: "#f0f8ff",
                                borderRadius: "8px",
                                border: "1px solid #b3d9ff"
                            }}
                        >
                            <div style={{marginBottom: "16px"}}>
                                <label style={{fontSize: "16px", fontWeight: "600", color: "#262626", display: "block", marginBottom: "8px"}}>
                                    Credit Log Başlangıç Tarihi
                                </label>
                                <ConfigProvider locale={trTR}>
                                    <DatePicker
                                        placeholder="Credit log başlangıç tarihini seçin"
                                        style={{width: "100%"}}
                                        onChange={handleDateChange}
                                        format="DD.MM.YYYY HH:mm"
                                        showTime
                                        allowClear={false}
                                        disabled={summaryLoading}
                                        getPopupContainer={trigger => trigger.parentNode}
                                        showNow={false}
                                        value={creditLogStartDate}
                                    />
                                </ConfigProvider>
                                {dateError && (
                                    <Alert
                                        message={dateError}
                                        type="error"
                                        style={{marginTop: "12px"}}
                                        showIcon
                                    />
                                )}
                                <div style={{marginTop: "8px", fontSize: "13px", color: "#666", fontStyle: "italic"}}>
                                    En erken tarih: {moment(previewData.lastDeactivateDate).format("DD.MM.YYYY HH:mm")}
                                </div>
                                
                                {/* Credit Log Özeti */}
                                {creditLogStartDate && (
                                    <div style={{marginTop: "16px"}}>
                                        {summaryLoading ? (
                                            <div style={{
                                                padding: "16px",
                                                backgroundColor: "#f0f8ff",
                                                border: "1px solid #b3d9ff",
                                                borderRadius: "6px",
                                                textAlign: "center"
                                            }}>
                                                <Spin size="small" /> Credit log bilgileri yükleniyor...
                                            </div>
                                        ) : creditLogSummary ? (
                                            <div style={{
                                                padding: "16px",
                                                backgroundColor: "#f0f8ff",
                                                border: "1px solid #b3d9ff",
                                                borderRadius: "6px"
                                            }}>
                                                <div style={{fontSize: "14px", color: "#666", marginBottom: "4px"}}>
                                                    Seçilen tarihten itibaren:
                                                </div>
                                                <div style={{fontSize: "16px", fontWeight: "600", color: "#262626"}}>
                                                    <strong>{creditLogSummary.creditLogCount || 0}</strong> adet credit log bulundu
                                                </div>
                                                <div style={{fontSize: "16px", fontWeight: "bold", marginTop: "8px", color: "#1890ff"}}>
                                                    Toplam Tutar: {formatCurrency(creditLogSummary.totalCreditAmount || 0)}
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{
                                                padding: "16px",
                                                backgroundColor: "#f0f8ff",
                                                border: "1px solid #b3d9ff",
                                                borderRadius: "6px"
                                            }}>
                                                <div style={{fontSize: "14px", color: "#666"}}>
                                                    Seçilen tarihten itibaren credit log bulunamadı.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Space>
            ) : (
                <div style={{textAlign: "center", padding: "40px"}}>
                    Aktivasyon bilgileri yüklenemedi
                </div>
            )}
            
            <div 
                style={{
                    borderTop: "1px solid #f0f0f0",
                    paddingTop: "20px",
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "12px"
                }}
            >
                <Button 
                    onClick={onCancel} 
                    size="large"
                    style={{
                        minWidth: "100px",
                        height: "40px"
                    }}
                >
                    İptal
                </Button>
                <Button 
                    type="primary" 
                    onClick={handleConfirm}
                    disabled={loading || summaryLoading}
                    size="large"
                    style={{
                        minWidth: "140px",
                        height: "40px",
                        fontWeight: "500"
                    }}
                >
                    {summaryLoading ? "Credit Log Yükleniyor..." : "Cüzdanı Aktive Et"}
                </Button>
            </div>
            </div>
        </div>
    );
};
